import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CalendarIcon, Plus, Loader2 } from "lucide-react";

interface AddServiceLocationToOnboardingSheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function AddServiceLocationToOnboardingSheet({
  open,
  onOpenChange,
}: AddServiceLocationToOnboardingSheetProps) {
  const navigate = useNavigate();
  const [serviceLocationId, setServiceLocationId] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch real data
  const users = useQuery(api.users.list) || [];
  const allServiceLocations = useQuery(api.serviceLocations.getServiceLocations) || [];
  const createOnboarding = useMutation(api.clientOnboarding.create);

  // Get the selected service location details
  const selectedLocation = allServiceLocations.find((loc: any) => loc._id === serviceLocationId);

  // Reset form when sheet opens/closes
  useEffect(() => {
    if (!open) {
      setServiceLocationId("");
      setAssignedTo("");
      setStartDate(undefined);
      setDueDate(undefined);
      setNotes("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serviceLocationId || !assignedTo || !startDate || !dueDate) {
      alert("Please fill in all required fields");
      return;
    }

    if (!selectedLocation) {
      alert("Please select a valid service location");
      return;
    }

    setIsSubmitting(true);

    try {
      const onboardingId = await createOnboarding({
        businessId: selectedLocation.businessId,
        serviceLocationId: serviceLocationId as Id<"serviceLocations">,
        startDate: format(startDate, "yyyy-MM-dd"),
        dueDate: format(dueDate, "yyyy-MM-dd"),
        assignedTo: assignedTo as Id<"users">,
        notes: notes || undefined,
      });

      // Navigate to the new onboarding page
      if (onOpenChange) onOpenChange(false);
      navigate(`/clients/onboarding/${onboardingId}`);
    } catch (error) {
      console.error("Error adding service location to onboarding:", error);
      alert("Failed to create onboarding record. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" /> Add Location to Onboarding
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto sm:max-w-md md:max-w-lg">
        <SheetHeader>
          <SheetTitle>Add Service Location to Onboarding</SheetTitle>
          <SheetDescription>
            Add a new service location to the onboarding process. Select the service location to get
            started.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="serviceLocationId">
              Service Location <span className="text-red-500">*</span>
            </Label>
            <Select value={serviceLocationId} onValueChange={setServiceLocationId} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a service location" />
              </SelectTrigger>
              <SelectContent>
                {allServiceLocations.map((location: any) => (
                  <SelectItem key={location._id} value={location._id}>
                    {location.name} ({location.businessName})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedLocation && (
            <div className="space-y-2">
              <Label>Client</Label>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900">{selectedLocation.businessName}</p>
                <p className="text-xs text-gray-500">Auto-selected based on service location</p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="assignedTo">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a team member" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user: any) => (
                  <SelectItem key={user._id} value={user._id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="startDate">Service Start Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "PPP") : "Select start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dueDate">Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Select due date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any special instructions or notes for this onboarding..."
              rows={3}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Add to Onboarding"
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
